/**
 * XT-920 — the transfers a Mini Planner trip adds on its own:
 *
 * - the arrival, on the trip's first day: the airport to the first chapter's
 *   hotel;
 * - one between chapters, on a chapter's first day: the previous chapter's
 *   hotel to this one's (none when both are in the same town);
 * - the departure, on the trip's last day: the last chapter's hotel to the
 *   airport.
 *
 * Like a day's Guide and Transport (XT-828) they are never stored as items:
 * they are derived from the chapters every time, and the draft only records
 * what the agent changed on the day: the ones removed (`transfersRemoved`)
 * and the cars, or a flight or train, chosen for one (`transferVehicles`).
 * Without a choice the whole party rides the vehicle that seats it. Each car
 * is a line priced from the supplier's Transfer cell for the route (XT-145),
 * else the Default-pricing Transport; a flight's or train's price is not known
 * (on request, XT-992). An arrival or departure at an airport may also carry the
 * VIP airport assistance the agent added (`transferAssistance`, T-423), and
 * any transfer the time the agent set (`transferTimes`, T-425).
 */
import type { TransportRateLookupRow } from '../pricing/transport-rate-sheet';
import type {
  PlannerServiceRate,
  PlannerServiceRates,
  PlannerTransportVehicle,
} from './planner-day-services';
import {
  lookupTransportRouteRate,
  normalizePlaceKey,
} from '../pricing/transport-rate-sheet';
import { clockToMinutes, isPlannerClock } from './planner-day-clock';
import {
  plannerCellRate,
  plannerChapterHotel,
  plannerTransportUnits,
  plannerVehicle,
} from './planner-day-services';

export const PLANNER_TRANSFER_KINDS = [
  'arrival',
  'between',
  'departure',
] as const;
export type PlannerTransferKind = (typeof PLANNER_TRANSFER_KINDS)[number];

/** When each transfer starts unless the agent sets its time (T-425). */
export const PLANNER_TRANSFER_CLOCK: Readonly<
  Record<PlannerTransferKind, string>
> = {
  arrival: '09:00',
  between: '10:00',
  departure: '11:00',
};

/** The time the agent set on a transfer; no end: as long as it takes. */
export type PlannerTransferTime = { start: string; end?: string | null };

export type PlannerTransferTimes = Partial<
  Record<PlannerTransferKind, PlannerTransferTime | null>
>;

/** A stored transfer time, when it is one; an end not after the start drops. */
export const plannerTransferTime = (
  value: unknown
): PlannerTransferTime | null => {
  if (!value || typeof value !== 'object') return null;
  const { start, end } = value as Partial<PlannerTransferTime>;
  if (!isPlannerClock(start)) return null;
  return {
    start,
    end:
      isPlannerClock(end) && clockToMinutes(end) > clockToMinutes(start)
        ? end
        : null,
  };
};

/** The vehicle a transfer flown instead of driven is booked as. */
export const PLANNER_FLIGHT = 'Flight';
/** The vehicle a transfer taken by train is booked as (XT-992). */
export const PLANNER_TRAIN = 'Train';

/**
 * A transfer not driven: the party takes a flight or a train, one line whose
 * price is not known (on request). Each is also XT's transport type and the
 * product subcategory it books.
 */
export const PLANNER_ON_REQUEST_LEGS = [PLANNER_FLIGHT, PLANNER_TRAIN] as const;
export type PlannerOnRequestLeg = (typeof PLANNER_ON_REQUEST_LEGS)[number];

/** The flight or train among the chosen vehicles, else null (cars). */
export const plannerOnRequestLeg = (
  vehicles: readonly { type: string }[] | null | undefined
): PlannerOnRequestLeg | null =>
  PLANNER_ON_REQUEST_LEGS.find((leg) =>
    (vehicles ?? []).some((entry) => entry.type === leg)
  ) ?? null;

/** Where an arrival starts and a departure ends unless the agent says. */
export const PLANNER_AIRPORT = 'Airport';
/** The kinds of place an arrival or departure can use; any other is typed. */
export const PLANNER_TRANSFER_PLACES = [
  PLANNER_AIRPORT,
  'Train station',
  'Port',
] as const;
// How a supplier's table may name each kind of place; one word matching is
// enough. Never "Porto": it is a city.
const PLACE_WORDS: Record<string, string> = {
  Airport: 'Airport Aeroporto Aeropuerto Aeroport',
  'Train station': 'Train Station Estacao Estacion Gare Bahnhof Stazione',
  Port: 'Port Harbour Harbor Cruise Terminal Marina',
};

type TransferPlace = {
  id?: string;
  kind?: string;
  title?: string | null;
  subtitle?: string | null;
  location?: { lat: number; lng: number } | null;
};

export type PlannerTransferLeg = {
  id: string;
  regionTitle?: string | null;
  baseHotel?: TransferPlace | null;
  days: ReadonlyArray<{
    id: string;
    items?: readonly TransferPlace[];
    transfersRemoved?: readonly string[] | null;
    transferVehicles?: PlannerTransferVehicles | null;
    /** Where the arrival starts / the departure ends; absent: the airport. */
    transferPlaces?: PlannerTransferPlaces | null;
    /** The VIP airport assistance added to the arrival / departure (T-423). */
    transferAssistance?: PlannerTransferAssistances | null;
    /** The time the agent set per transfer (T-425); absent: its usual one. */
    transferTimes?: PlannerTransferTimes | null;
  }>;
};

/**
 * The VIP airport assistance on an arrival or departure: the DMC's product
 * at that airport, copied when added; without one (`productId` null) it is
 * on request and the DMC books it.
 */
export type PlannerTransferAssistance = {
  productId: string | null;
  /**
   * The route it was added for (`plannerAssistanceRoute`): another airport or
   * town, or the departure moving to a new chapter, leaves it behind.
   */
  route: string;
  title: string;
  clientPrice: number | null;
  childPrice?: number | null;
  currency: string | null;
  pricingType: string | null;
};

export type PlannerTransferAssistances = Partial<
  Record<'arrival' | 'departure', PlannerTransferAssistance | null>
>;

/** A stored assistance, when it is one. */
export const plannerTransferAssistance = (
  value: unknown
): PlannerTransferAssistance | null => {
  if (!value || typeof value !== 'object') return null;
  const entry = value as Partial<PlannerTransferAssistance>;
  if (typeof entry.title !== 'string' || !entry.title.trim()) return null;
  if (typeof entry.route !== 'string' || !entry.route) return null;
  const amount = (raw: unknown) =>
    typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
  const text = (raw: unknown) =>
    typeof raw === 'string' && raw.trim() ? raw : null;
  return {
    productId: text(entry.productId),
    route: entry.route,
    title: entry.title.trim(),
    clientPrice: amount(entry.clientPrice),
    childPrice: amount(entry.childPrice),
    currency: text(entry.currency),
    pricingType: text(entry.pricingType),
  };
};

/**
 * The agent's start of the arrival, end of the departure (on its day): a kind
 * of place (`PLANNER_TRANSFER_PLACES`), or one picked on the map.
 */
export type PlannerTransferPlaces = Partial<
  Record<'arrival' | 'departure', string | PlannerTransferSpot | null>
>;

/** A place picked from Google: where the car picks up or drops off. */
export type PlannerTransferSpot = {
  /** "Humberto Delgado Airport" — what the route reads. */
  name: string;
  /** The full address Google gave. */
  address?: string | null;
  latitude: number;
  longitude: number;
  city?: string | null;
  country?: string | null;
  /** IANA zone Google's point resolves to; empty over the sea. */
  timezone?: string | null;
};

/** A stored place as a spot, when it is one. */
export const plannerTransferSpot = (
  value: unknown
): PlannerTransferSpot | null => {
  if (!value || typeof value !== 'object') return null;
  const spot = value as Partial<PlannerTransferSpot>;
  return typeof spot.name === 'string' &&
    spot.name.trim() &&
    typeof spot.latitude === 'number' &&
    typeof spot.longitude === 'number'
    ? (spot as PlannerTransferSpot)
    : null;
};

/** The cars (or a flight or train) the agent chose per transfer on a day. */
export type PlannerTransferVehicles = Partial<
  Record<PlannerTransferKind, PlannerTransportVehicle[] | null>
>;

export type PlannerTransfer = {
  kind: PlannerTransferKind;
  /** `transfer:arrival`, `transfer:departure`, `transfer:<chapter id>`. */
  key: string;
  dayId: string;
  /** The chapter the day is in. */
  legIndex: number;
  /** The day's place in its chapter. */
  dayInLeg: number;
  /** The chapter it leaves from, for a transfer between chapters. */
  fromLegIndex: number | null;
  /**
   * What the route is priced by: a chapter's town, or the arrival's start /
   * departure's end (`PLANNER_AIRPORT`, another kind of place, or typed).
   */
  from: string;
  to: string;
  /** What the route reads: a chapter's hotel by name, else the same. */
  fromLabel: string;
  toLabel: string;
  /** The arrival's start or the departure's end (by name); null between chapters. */
  place: string | null;
  /** That place when picked on the map: its point and address. */
  spot: PlannerTransferSpot | null;
  /** The agent removed it: it is shown to add back, never priced or booked. */
  removed: boolean;
  /** The cars the agent chose; null: the vehicle that seats the party. */
  vehicles: PlannerTransportVehicle[] | null;
  /** The agent chose a flight or a train instead of a car; null: cars. */
  onRequest: PlannerOnRequestLeg | null;
  /** Its end outside the trip is an airport: VIP assistance can be added. */
  atAirport: boolean;
  /** The VIP airport assistance the agent added; null: none (T-423). */
  assistance: PlannerTransferAssistance | null;
  /** HH:MM it starts: the agent's time, else `PLANNER_TRANSFER_CLOCK`. */
  startTime: string;
  /** HH:MM the agent says it ends; null: not set. */
  endTime: string | null;
  /** The agent set its time (T-425). */
  timeSet: boolean;
};

/** One car of a transfer, or its flight or train: what is priced and booked. */
export type PlannerTransferLine = {
  /** The transfer's key; its second car `…#2`, and so on. */
  key: string;
  /** A planner vehicle type, or the flight or train. */
  vehicle: string;
  seats: number | null;
  /** The travellers it carries: the cars fill in order, each to its seats. */
  pax: number;
  /** The car was chosen by the agent, not suggested. */
  picked: boolean;
  /** A flight or train, priced on request; null: a car. */
  onRequest: PlannerOnRequestLeg | null;
};

/** The town a chapter sleeps in: its hotel's, else the region's name. */
export const plannerChapterTown = (leg: PlannerTransferLeg): string =>
  plannerChapterHotel(leg)?.subtitle?.trim() || leg.regionTitle?.trim() || '';

/** Where a chapter's transfers start or end: its hotel, else its region. */
const chapterStop = (leg: PlannerTransferLeg) => {
  const town = plannerChapterTown(leg);
  const hotel = (plannerChapterHotel(leg) as TransferPlace | undefined)?.title;
  return { town, label: hotel?.trim() || town };
};

const transferKey = (kind: PlannerTransferKind, legId: string) =>
  kind === 'between' ? `transfer:${legId}` : `transfer:${kind}`;

/** The trip's transfers in day order, removed ones flagged. */
export const plannerTransfers = (
  legs: readonly PlannerTransferLeg[]
): PlannerTransfer[] => {
  const withDays = legs.flatMap((leg, legIndex) =>
    leg.days.length > 0 ? [{ leg, legIndex }] : []
  );
  const first = withDays[0];
  const last = withDays.at(-1);
  if (!first || !last) return [];
  const make = (
    kind: PlannerTransferKind,
    at: { leg: PlannerTransferLeg; legIndex: number },
    dayInLeg: number,
    route: (
      place: string,
      priced: string
    ) => {
      from: { town: string; label: string };
      to: { town: string; label: string };
      fromLegIndex?: number;
    }
  ): PlannerTransfer => {
    const day = at.leg.days[dayInLeg] as PlannerTransferLeg['days'][number];
    const chosen = day.transferVehicles?.[kind];
    const stored = kind === 'between' ? null : day.transferPlaces?.[kind];
    const spot = plannerTransferSpot(stored);
    const place =
      kind === 'between'
        ? null
        : spot
          ? spot.name.trim()
          : (typeof stored === 'string' && stored.trim()) || PLANNER_AIRPORT;
    const added = atAirportKind(kind)
      ? plannerTransferAssistance(day.transferAssistance?.[kind])
      : null;
    const atAirport =
      kind !== 'between' &&
      (spot ? isAirportName(spot.name) : place === PLANNER_AIRPORT);
    // A picked place is priced by its name and town ("… Airport Lisbon").
    const { from, to, fromLegIndex } = route(
      place ?? '',
      spot ? [spot.name, spot.city].filter(Boolean).join(' ') : (place ?? '')
    );
    const time = plannerTransferTime(day.transferTimes?.[kind]);
    const assistanceRoute = plannerAssistanceRoute({
      from: from.town,
      to: to.town,
    });
    return {
      kind,
      key: transferKey(kind, at.leg.id),
      dayId: day.id,
      legIndex: at.legIndex,
      dayInLeg,
      fromLegIndex: fromLegIndex ?? null,
      from: from.town,
      to: to.town,
      fromLabel: from.label,
      toLabel: to.label,
      place,
      spot,
      removed: (day.transfersRemoved ?? []).includes(kind),
      vehicles: chosen && chosen.length > 0 ? [...chosen] : null,
      onRequest: plannerOnRequestLeg(chosen),
      atAirport,
      assistance: atAirport && added?.route === assistanceRoute ? added : null,
      startTime: time?.start ?? PLANNER_TRANSFER_CLOCK[kind],
      endTime: time?.end ?? null,
      timeSet: time != null,
    };
  };
  const outside = (place: string, priced: string) => ({
    town: priced,
    label: place,
  });
  const transfers: PlannerTransfer[] = [
    make('arrival', first, 0, (place, priced) => ({
      from: outside(place, priced),
      to: chapterStop(first.leg),
    })),
  ];
  withDays.forEach((at, index) => {
    const previous = withDays[index - 1];
    if (!previous) return;
    const from = chapterStop(previous.leg);
    const to = chapterStop(at.leg);
    if (
      from.town &&
      normalizePlaceKey(from.town) === normalizePlaceKey(to.town)
    )
      return;
    transfers.push(
      make('between', at, 0, () => ({
        from,
        to,
        fromLegIndex: previous.legIndex,
      }))
    );
  });
  transfers.push(
    make('departure', last, last.leg.days.length - 1, (place, priced) => ({
      from: chapterStop(last.leg),
      to: outside(place, priced),
    }))
  );
  return transfers;
};

/** What an XT transfer line records of its time, to see the agent change it. */
export const plannerTransferTimeTag = (
  transfer: Pick<PlannerTransfer, 'startTime' | 'endTime'>
): string => `${transfer.startTime}-${transfer.endTime ?? ''}`;

/** "Arrival transfer", "Transfer", "Departure transfer". */
export const plannerTransferLabel = (kind: PlannerTransferKind): string =>
  kind === 'arrival'
    ? 'Arrival transfer'
    : kind === 'departure'
      ? 'Departure transfer'
      : 'Transfer';

/**
 * A transfer's lines for `pax` travellers: its flight or train, else one per
 * car the agent chose, else the one vehicle that seats them.
 */
export const plannerTransferLines = (
  transfer: Pick<PlannerTransfer, 'key' | 'vehicles' | 'onRequest'>,
  pax: number
): PlannerTransferLine[] => {
  const party = Math.max(1, pax);
  if (transfer.onRequest) {
    return [
      {
        key: transfer.key,
        vehicle: transfer.onRequest,
        seats: null,
        pax: party,
        picked: true,
        onRequest: transfer.onRequest,
      },
    ];
  }
  const units = plannerTransportUnits(transfer.vehicles);
  if (units.length === 0) {
    const vehicle = plannerVehicle(party);
    return [
      {
        key: transfer.key,
        vehicle: vehicle.type,
        seats: vehicle.seats,
        pax: party,
        picked: false,
        onRequest: null,
      },
    ];
  }
  let seatsBefore = 0;
  return units.map((unit, index) => {
    const line = {
      key: index === 0 ? transfer.key : `${transfer.key}#${index + 1}`,
      vehicle: unit.type,
      seats: unit.seats,
      pax: Math.max(1, Math.min(unit.seats, party - seatsBefore)),
      picked: true,
      onRequest: null,
    };
    seatsBefore += unit.seats;
    return line;
  });
};

const routePlace = (place: string) => PLACE_WORDS[place] ?? place;

const AIRPORT_WORDS = new Set(
  normalizePlaceKey(PLACE_WORDS[PLANNER_AIRPORT] ?? '').split(' ')
);

/** A picked place's name says it is an airport ("Humberto Delgado Airport"). */
const isAirportName = (name: string): boolean =>
  normalizePlaceKey(name)
    .split(' ')
    .some((word) => AIRPORT_WORDS.has(word));

const atAirportKind = (
  kind: PlannerTransferKind
): kind is 'arrival' | 'departure' => kind !== 'between';

/**
 * The route an assistance is added for: the places it is priced by (the
 * airport, the hotel's town), so another hotel in the same town keeps it.
 */
export const plannerAssistanceRoute = (
  transfer: Pick<PlannerTransfer, 'from' | 'to'>
): string =>
  `${normalizePlaceKey(transfer.from)} > ${normalizePlaceKey(transfer.to)}`;

/** "Arrival VIP assistance", "Departure VIP assistance". */
export const plannerAssistanceLabel = (
  kind: 'arrival' | 'departure'
): string =>
  kind === 'arrival' ? 'Arrival VIP assistance' : 'Departure VIP assistance';

/**
 * What one car of a transfer costs for the `pax` travellers it carries: the
 * Transfer cell for its route and its vehicle (else the one that seats them),
 * from each chapter's region sheets in turn
 * (`rates`, the chapter it lands in first), then the country's default
 * sheets; else the Default-pricing Transport of the first.
 */
export const plannerTransferRate = (
  rates: readonly PlannerServiceRates[],
  query: {
    from: string;
    to: string;
    pax: number;
    onDate?: string | null;
    /** The car the agent chose: a cell must seat as many as it does. */
    vehicle?: string | null;
    seats?: number | null;
  }
): PlannerServiceRate | null => {
  const [own] = rates;
  if (!own) return null;
  const from = query.from.trim();
  const to = query.to.trim();
  const vehicle = query.vehicle ?? plannerVehicle(query.pax).type;
  const priceFrom = (cells: readonly TransportRateLookupRow[]) =>
    from && to
      ? lookupTransportRouteRate(cells, {
          from: routePlace(from),
          to: routePlace(to),
          vehicle,
          pax: query.pax,
          onDate: query.onDate,
          country: own.country,
          seats: query.seats ?? null,
        })
      : null;
  const hit =
    rates.reduce<ReturnType<typeof priceFrom>>(
      (found, entry) => found ?? priceFrom(entry.transportCells),
      null
    ) ?? priceFrom(own.countryTransportCells);
  return plannerCellRate(own, hit);
};
